
import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, Edit2, Clock, FileText, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CalendarContentItem } from "@/hooks/useContentCalendar";
import { cn } from "@/lib/utils";

interface CalendarDayViewProps {
  currentDate: Date;
  items: CalendarContentItem[];
  onItemClick: (item: CalendarContentItem) => void;
}

export const CalendarDayView = ({ currentDate, items, onItemClick }: CalendarDayViewProps) => {
  const itemsForDay = useMemo(() => {
    const targetDate = format(currentDate, "yyyy-MM-dd");
    
    return items.filter(item => {
      // Check scheduledFor date if available
      if (item.scheduledFor) {
        const itemScheduledDate = format(new Date(item.scheduledFor), "yyyy-MM-dd");
        if (itemScheduledDate === targetDate) return true;
      }
      
      // Also check publishedAt date
      if (item.publishedAt) {
        const itemPublishedDate = format(new Date(item.publishedAt), "yyyy-MM-dd");
        if (itemPublishedDate === targetDate) return true;
      }
      
      return false;
    });
  }, [items, currentDate]);
  
  // Get content type icon
  const getContentTypeIcon = (item: CalendarContentItem) => {
    if (item.sourceType === 'blog') {
      return <FileText className="w-4 h-4 mr-2 text-blue-600" />;
    } else {
      return <Linkedin className="w-4 h-4 mr-2 text-blue-700" />;
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="text-lg font-medium">
        {format(currentDate, "EEEE")}, {format(currentDate, "PPP")}
      </div>
      
      {itemsForDay.length === 0 ? (
        <div className="p-8 text-center text-slate-500 border border-dashed border-slate-300 rounded-md my-4">
          No content scheduled for this day
        </div>
      ) : (
        <div className="space-y-3">
          {itemsForDay.map((item) => (
            <Card key={`${item.sourceType}-${item.id}`} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      {getContentTypeIcon(item)}
                      <h3 className="font-medium">{item.title}</h3>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                      {item.content ? (
                        <span dangerouslySetInnerHTML={{ 
                          __html: item.content.length > 120 
                            ? item.content.substring(0, 120) + "..." 
                            : item.content 
                        }} />
                      ) : "No content preview available"}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-2">
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {item.scheduledFor ? 
                          format(new Date(item.scheduledFor), "HH:mm") :
                          (item.publishedAt ? format(new Date(item.publishedAt), "HH:mm") : "")
                        }
                      </div>
                      
                      <Badge 
                        className={cn(
                          item.status === 'published' ? "bg-green-100 text-green-800 hover:bg-green-200" :
                          item.status === 'scheduled' ? "bg-blue-100 text-blue-800 hover:bg-blue-200" :
                          "bg-amber-100 text-amber-800 hover:bg-amber-200"
                        )}
                        variant="outline"
                      >
                        {item.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0"
                      onClick={() => onItemClick(item)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0"
                      onClick={() => onItemClick(item)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
